import { useQuery, gql, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useLocation } from 'react-router-dom'

import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import Heading from 'components/v2/Heading'
import { Button } from 'components/v2/Button'
import { Content, ContentBody, ContentHeader } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { SitesFilters } from './Filters'
import { SitesList } from './List'
import { SitesPagination } from './Pagination'
import { WITHOUT_USER } from 'resolvers/sites'
import { filterEmptyValue } from 'utils/object'
import { getQueries } from 'utils/route'
import { canRead, canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { GET_USERS_SUGGESTIONS } from 'features/Users/queries-legacy'
import { GTUserSuggestionsResponse } from 'gql/types.generated'

const LIMIT = 30

const messages = defineMessages({
  helmet: {
    id: 'sites.helmet',
    defaultMessage: 'Point of Sales | Nexbus Admin'
  }
})

const GET_SITES = gql`
  query GetSites($limit: Int!, $page: Int!, $type: SiteType, $userId: ID, $name: String, $withoutUser: Boolean) {
    sites(
      pagination: { size: $limit, page: $page }
      input: { type: $type, userID: $userId, name: $name, withoutUser: $withoutUser }
    ) {
      total
      items {
        id
        name
        isActive
        type
        users {
          id
          username
          firstName
          lastName
        }
        company {
          id
          name
        }
      }
    }
  }
`

const SitesView = (): ReactElement => {
  const location = useLocation()
  const intl = useIntl()
  const login = useLogin()

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  let content
  const { page = '1', type = '', user = '', name = '' } = getQueries(location.search)

  const pageNb = parseInt(page)

  const { data: userData } = useQuery<{ userSuggestions: GTUserSuggestionsResponse }>(GET_USERS_SUGGESTIONS, {
    skip: !user || user === WITHOUT_USER.id,
    variables: {
      input: {
        id: user
      }
    }
  })
  const {
    loading,
    error,
    data = {},
    refetch,
    networkStatus
  } = useQuery(GET_SITES, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: filterEmptyValue({
      limit: LIMIT,
      page: pageNb - 1,
      type,
      userId: user === WITHOUT_USER.id ? '' : user,
      name,
      withoutUser: user === WITHOUT_USER.id
    })
  })

  useEffect(() => {
    refetch()
  }, [])

  const filters = {
    type,
    user: userData?.userSuggestions.items?.at(0) || {
      id: '',
      username: '',
      firstName: '',
      lastName: '',
      name: ''
    },
    name
  }

  if (!canRead(FEATURES.SITE, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.SITE} />
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="sites.error.description"
            defaultMessage="There was an error while loading your point of sales"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button
            onClick={(): void => {
              refetch()
            }}
            kind="primary"
          >
            <FormattedMessage id="sites.error.action" defaultMessage="Reload your point of sales" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.sites.items) && pageNb === 1 && isEmpty(filterEmptyValue(filters))) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="sites.empty.description" defaultMessage="You don&#39;t have any point of sales yet" />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button as={NavLink} to="/sites/new" kind="primary">
            <FormattedMessage id="sites.empty.action" defaultMessage="Add your first point of sale" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.sites.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="sites.empty.filters.description"
            defaultMessage="There was no point of sales found according to your filters"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button as={NavLink} to="/sites" kind="primary">
            <FormattedMessage id="sites.empty.filters.action" defaultMessage="Reset filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <SitesList sites={data.sites.items} />

        <SitesPagination
          page={pageNb}
          limit={LIMIT}
          totalItems={data.sites.total}
          currentItemsCount={data.sites.items.length}
          filters={filters}
        />
      </>
    )
  }

  return (
    <Content>
      <ContentHeader isResponsive>
        <Heading.H3>
          <FormattedMessage id="sites.header.label" defaultMessage="Point of sales" />
        </Heading.H3>
        {canWrite(FEATURES.SITE, login.accessToken.policies) && (
          <Button as={NavLink} to="/sites/new" kind="primary">
            <FormattedMessage id="sites.header.new" defaultMessage="+ Create point of sale" />
          </Button>
        )}
      </ContentHeader>
      <ContentBody>
        <SitesFilters filters={filters} />
        <section>{content}</section>
      </ContentBody>
    </Content>
  )
}

export default withLastUrl(SitesView)
